import { useUnitDisplay } from '../../app/units/UnitDisplayContext';

import {
  deliveredBarrelSummary,
  describeFeasibleChange,
} from './solutionSummary';
import type {
  DetailedOptimizationSolution,
  HoseInventoryUsage,
  OptimizationRunRecord,
  ScenarioOptimizationResult,
} from './types';

export function SolutionDetails({
  baseline,
  label,
  run,
  solution,
}: {
  baseline?: DetailedOptimizationSolution;
  label: string;
  run: OptimizationRunRecord;
  solution: DetailedOptimizationSolution;
}) {
  const { formatSiValue } = useUnitDisplay();
  const siteName = (id: string) =>
    run.request.network_project.candidate_pump_sites.find(
      (site) => site.id === id,
    )?.name ?? id;
  const scenarioName = (id: string) =>
    run.request.network_project.scenarios.find((scenario) => scenario.id === id)
      ?.name ?? id;
  const unitName = (id: string) =>
    solution.physical_units.find(({ fleet_unit: unit }) => unit.id === id)
      ?.fleet_unit.physical_identifier ??
    baseline?.physical_units.find(({ fleet_unit: unit }) => unit.id === id)
      ?.fleet_unit.physical_identifier ??
    id;
  const feasibleChange =
    baseline === undefined
      ? null
      : describeFeasibleChange(
          baseline,
          solution,
          unitName,
          siteName,
          scenarioName,
        );
  const deliverySummary = (scenario: ScenarioOptimizationResult) =>
    deliveredBarrelSummary(
      scenario,
      run.request.network_project.scenarios.find(
        (candidate) => candidate.id === scenario.scenario_id,
      ),
    );
  const hoseDetails = (hose: HoseInventoryUsage) => {
    const route = `${formatSiValue(hose.route_length_m, 'head')} · ${
      hose.link_count
    } ${hose.link_count === 1 ? 'link' : 'links'}`;
    if (!hose.hose_type_present) return `${route} · hose type missing`;
    if (!hose.inventory_tracked) return `${route} · inventory not recorded`;
    const sections = hose.sections
      .map(
        (section) =>
          `${section.used_count === null ? '?' : section.used_count} of ${
            section.available_count
          } × ${formatSiValue(section.hose_length_m, 'head')}`,
      )
      .join(', ');
    return `${route} · ${sections}`;
  };

  return (
    <article className="optimization-solution">
      <h4>{label}</h4>
      <p className="optimization-solution__summary">
        {solution.objective.physical_pump_count}{' '}
        {solution.objective.physical_pump_count === 1 ? 'pump' : 'pumps'} ·{' '}
        {solution.objective.occupied_site_count}{' '}
        {solution.objective.occupied_site_count === 1 ? 'site' : 'sites'}
      </p>
      {feasibleChange === null ? null : (
        <p>
          <strong>Feasible change:</strong> {feasibleChange}
        </p>
      )}
      <h5>Pump plan</h5>
      {solution.physical_units.length === 0 &&
      solution.physical_pump_banks.length === 0 ? (
        <p>No pumps.</p>
      ) : (
        <ul>
          {solution.physical_units.map(
            ({ fleet_unit: unit, pump_template: template }) => (
              <li key={unit.id}>
                {unit.physical_identifier} · {template.manufacturer}{' '}
                {template.model} ({template.revision})
              </li>
            ),
          )}
          {solution.physical_pump_banks.map((bank) => (
            <li key={`${bank.candidate_site_id}-${bank.arrangement}`}>
              {siteName(bank.candidate_site_id)} · {bank.arrangement} · stages{' '}
              {bank.stages.map((stage) => stage.length).join(' + ')} ·{' '}
              {bank.orientation.replaceAll('_', ' ')}
            </li>
          ))}
        </ul>
      )}
      {(solution.hose_usage ?? []).length === 0 ? null : (
        <>
          <h5>Hose plan</h5>
          <ul>
            {(solution.hose_usage ?? []).map((hose) => (
              <li key={hose.hose_type_id}>
                <strong>{hose.hose_model}</strong> · {hoseDetails(hose)}
              </li>
            ))}
          </ul>
        </>
      )}
      <p>
        <strong>Limiting checks:</strong>{' '}
        {solution.limiting_constraints.join(', ') || 'none reported'}
      </p>
      {solution.scenarios.map((scenario) => (
        <section className="optimization-scenario" key={scenario.scenario_id}>
          <h5>{scenarioName(scenario.scenario_id)}</h5>
          {deliverySummary(scenario) === null ? null : (
            <p>
              <strong>Delivered:</strong>{' '}
              {deliverySummary(scenario)}
            </p>
          )}
          <p>
            Unit settings:{' '}
            {scenario.unit_settings
              .map((unit) =>
                unit.enabled && unit.engine_speed_rpm !== null
                  ? `${unitName(
                      unit.fleet_unit_id,
                    )} · engine ${unit.engine_speed_rpm.toLocaleString(
                      'en-US',
                    )} RPM`
                  : `${unitName(unit.fleet_unit_id)} ${
                      unit.enabled ? '· engine RPM unavailable' : 'disabled'
                    }`,
              )
              .join(', ') || 'none'}
          </p>
          {scenario.pump_banks.map((bank) => (
            <p key={`${scenario.scenario_id}-${bank.candidate_site_id}`}>
              {siteName(bank.candidate_site_id)}:{' '}
              {bank.stages
                .flatMap((stage) => stage.units)
                .map(
                  (unit) =>
                    `${unitName(
                      unit.fleet_unit_id,
                    )} · engine ${unit.engine_speed_rpm.toLocaleString(
                      'en-US',
                    )} RPM${
                      unit.enabled ? '' : ' (disabled)'
                    }`,
                )
                .join(', ')}
            </p>
          ))}
        </section>
      ))}
      <details className="optimization-details optimization-solution__details">
        <summary>Performance details</summary>
        <dl className="optimization-metrics">
          <div>
            <dt>Worst normalized margin</dt>
            <dd>
              {solution.objective.worst_normalized_safety_margin.toFixed(3)}
            </dd>
          </div>
          <div>
            <dt>Weighted brake power</dt>
            <dd>
              {formatSiValue(solution.objective.weighted_brake_power_w, 'power')}
            </dd>
          </div>
          <div>
            <dt>Mobilization distance</dt>
            <dd>
              {solution.objective.mobilization_distance_m === null
                ? '—'
                : formatSiValue(
                    solution.objective.mobilization_distance_m,
                    'head',
                  )}
            </dd>
          </div>
        </dl>
        {solution.scenarios.map((scenario) => (
          <section
            className="optimization-scenario-performance"
            key={`performance-${scenario.scenario_id}`}
          >
            <h5>{scenarioName(scenario.scenario_id)} performance</h5>
            <dl className="optimization-metrics">
              <div>
                <dt>Normalized margin</dt>
                <dd>{scenario.normalized_safety_margin.toFixed(3)}</dd>
              </div>
              <div>
                <dt>Brake power</dt>
                <dd>{formatSiValue(scenario.brake_power_w, 'power')}</dd>
              </div>
            </dl>
            <div className="optimization-operating-points">
              {scenario.simulation_result.pump_operating_points.map((point) => (
                <dl key={`${scenario.scenario_id}-${point.fleet_unit_id}`}>
                  <div>
                    <dt>Unit</dt>
                    <dd>{unitName(point.fleet_unit_id)}</dd>
                  </div>
                  <div>
                    <dt>Engine RPM</dt>
                    <dd>
                      {point.engine_speed_rpm.toLocaleString('en-US')} RPM
                    </dd>
                  </div>
                  <div>
                    <dt>Pump RPM</dt>
                    <dd>
                      {point.pump_speed_rpm.toLocaleString('en-US', {
                        maximumFractionDigits: 2,
                      })}{' '}
                      RPM
                    </dd>
                  </div>
                  <div>
                    <dt>Efficiency</dt>
                    <dd>
                      {point.efficiency_fraction === null
                        ? '—'
                        : `${(point.efficiency_fraction * 100).toFixed(1)}%`}
                    </dd>
                  </div>
                  <div>
                    <dt>Flow</dt>
                    <dd>{formatSiValue(point.flow_m3_s, 'flowRate')}</dd>
                  </div>
                  <div>
                    <dt>Head</dt>
                    <dd>{formatSiValue(point.head_gain_m, 'head')}</dd>
                  </div>
                  <div>
                    <dt>Power</dt>
                    <dd>
                      {point.brake_power_w === null
                        ? '—'
                        : formatSiValue(point.brake_power_w, 'power')}
                    </dd>
                  </div>
                  <div>
                    <dt>Suction pressure (absolute)</dt>
                    <dd>
                      {point.suction_pressure_absolute_pa === null
                        ? '—'
                        : formatSiValue(
                            point.suction_pressure_absolute_pa,
                            'pressure',
                          )}
                    </dd>
                  </div>
                  <div>
                    <dt>NPSH margin</dt>
                    <dd>
                      {point.npsh_margin_m === null
                        ? '—'
                        : formatSiValue(point.npsh_margin_m, 'head')}
                    </dd>
                  </div>
                </dl>
              ))}
            </div>
          </section>
        ))}
      </details>
    </article>
  );
}
